import React, { useEffect, useState, useCallback, useMemo } from 'react'; // Ensure useMemo is imported
import { useNavigate } from 'react-router-dom';

import { AutoComplete, Button, DatePicker, Input, notification, Table, Tag, Tooltip } from 'antd'; // Ensure AutoComplete is imported
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

// Icon Imports
import { BiExport } from 'react-icons/bi';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

// CSS Imports
import '../../../css/UserAdmin/Global.css';

// Component Imports
import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(isBetween);

// const { RangePicker } = DatePicker; // Kept for potential future use

const ManageEnrollment = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    // State for data
    const [allEnrollments, setAllEnrollments] = useState([]); // Raw data from API
    const [loading, setLoading] = useState(true);

    // State for search and AutoComplete
    const [searchTerm, setSearchTerm] = useState('');
    const [autocompleteOptions, setAutocompleteOptions] = useState([]); // For AutoComplete suggestions

    // State for AntD table filters and sorter
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /**
     * Fetches all official enrollment records from the backend.
     */
    const fetchEnrollments = useCallback(async () => {
        setLoading(true);
        try {
            const fullUrl = `${process.env.REACT_APP_API_URL}/api/admin/enrollments`;
            const response = await fetch(fullUrl);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const result = await response.json();
            const enrollmentData = Array.isArray(result) ? result : (result.data && Array.isArray(result.data) ? result.data : []);

            if (!Array.isArray(enrollmentData)) {
                throw new Error(`Expected array but got ${typeof enrollmentData}`);
            }

            const processedData = enrollmentData.map(item => ({
                ...item,
                key: item._id, // Ensure unique key for AntD Table
            }));
            setAllEnrollments(processedData); // Store all fetched data
        } catch (error) {
            console.error('Error fetching enrollment records:', error);
            setAllEnrollments([]); // Clear data on error
            notification.error({
                message: 'Fetch Error',
                description: 'Failed to fetch enrollment records. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, []);

    // Effect to fetch enrollments on initial mount
    useEffect(() => {
        fetchEnrollments();
    }, [fetchEnrollments]);


    // --- Client-Side Filtering and Sorting using useMemo ---
    const dataSource = useMemo(() => {
        let processedData = [...allEnrollments];

        // 1. Apply global search term (from AutoComplete)
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            // Simple .includes() search. For fuzzy, integrate Fuse.js here.
            processedData = processedData.filter(item =>
                (item.studentID || '').toLowerCase().includes(searchLower) ||
                (item.fullName || '').toLowerCase().includes(searchLower) ||
                (item.email || '').toLowerCase().includes(searchLower) ||
                (item.academicYear || '').toLowerCase().includes(searchLower) ||
                (item.academicTerm || '').toLowerCase().includes(searchLower) ||
                (item.academicLevel || '').toLowerCase().includes(searchLower) ||
                (item.academicStrand || '').toLowerCase().includes(searchLower) ||
                (item.enrollmentStatus || '').toLowerCase().includes(searchLower) ||
                (item.paymentInfo?.referenceNumber || '').toLowerCase().includes(searchLower)
            );
        }

        // 2. Apply AntD table column filters
        Object.keys(tableFilters).forEach(key => {
            const filterValues = tableFilters[key];
            if (filterValues && filterValues.length > 0) {
                processedData = processedData.filter(item => {
                    const itemValue = key.startsWith('paymentInfo.') ? item.paymentInfo?.[key.split('.')[1]] : item[key];
                    return filterValues.includes(itemValue);
                });
            }
        });

        // 3. Apply AntD table sorter
        if (sorter.columnKey && sorter.order) {
            processedData.sort((a, b) => {
                let valA = a[sorter.columnKey];
                let valB = b[sorter.columnKey];

                // Special handling for nested properties
                if (sorter.columnKey === 'paymentRef') { // key for paymentInfo.referenceNumber
                    valA = a.paymentInfo?.referenceNumber;
                    valB = b.paymentInfo?.referenceNumber;
                }

                const safeValA = valA === undefined || valA === null ? '' : valA;
                const safeValB = valB === undefined || valB === null ? '' : valB;

                let comparison = 0;
                if (sorter.columnKey === 'enrolledDate') {
                    comparison = dayjs(safeValA).unix() - dayjs(safeValB).unix();
                } else if (typeof safeValA === 'string' && typeof safeValB === 'string') {
                    comparison = safeValA.localeCompare(safeValB);
                } else if (typeof safeValA === 'number' && typeof safeValB === 'number') {
                    comparison = safeValA - safeValB;
                } else {
                    comparison = String(safeValA).localeCompare(String(safeValB));
                }

                return sorter.order === 'ascend' ? comparison : -comparison;
            });
        }
        return processedData;
    }, [allEnrollments, searchTerm, tableFilters, sorter]);


    /**
     * Updates the searchTerm state when AutoComplete input changes or a suggestion is selected.
     * @param {string} value - The new search term.
     */
    const handleSearchInputChange = (value) => {
        setSearchTerm(value || ''); // Ensure searchTerm is not undefined
    };

    /**
     * Generates suggestions for AutoComplete based on current input.
     * @param {string} searchText - The text typed by the user in AutoComplete.
     */
    const handleAutocompleteSuggest = (searchText) => {
        if (!searchText) {
            setAutocompleteOptions([]);
            return;
        }
        const lowerSearchText = searchText.toLowerCase();
        // Fields to draw suggestions from
        const suggestionFields = ['studentID', 'fullName', 'email', 'academicYear', 'academicLevel', 'academicStrand'];
        const paymentRefField = (item) => item.paymentInfo?.referenceNumber;

        const suggestions = allEnrollments
            .flatMap(item => [
                ...suggestionFields.map(field => item[field]),
                paymentRefField(item)
            ])
            .filter(Boolean) // Remove any null or undefined values
            .map(value => String(value)) // Ensure all are strings
            .filter((value, index, self) => self.map(v => v.toLowerCase()).indexOf(value.toLowerCase()) === index) // Unique, case-insensitive
            .filter(value => value.toLowerCase().includes(lowerSearchText))
            .map(value => ({ value: value, label: value }));

        setAutocompleteOptions(suggestions.slice(0, 10)); // Limit suggestions
    };

    /**
     * Clears all active table filters, sorters, and the search term.
     */
    const handleClearFilters = () => {
        setTableFilters({});
        setSorter({});
        setSearchTerm('');
        setAutocompleteOptions([]); // Clear autocomplete suggestions
        // The useMemo for dataSource will re-calculate based on empty searchTerm.
    };

    /**
     * Manually refreshes the enrollment data.
     */
    const handleRefresh = () => {
        setSearchTerm(''); // Clear search
        setTableFilters({});
        setSorter({});
        setAutocompleteOptions([]);
        fetchEnrollments(); // Fetch fresh data
    };

    const createLog = async (action, detail) => {
        try {
            const userID = localStorage.getItem('userID');
            const accountName = localStorage.getItem('fullName');
            const role = localStorage.getItem('role');
            if (!userID || !accountName || !role) {
                console.warn('User info not found for logging.');
                return;
            }
            const logData = { userID, accountName, role, action, detail };
            // ... (rest of your createLog fetch call)
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData),
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.warn('System log for export failed to record properly:', errorData.message || response.statusText);
            } else {
                const data = await response.json();
                console.log('System log for export recorded:', data);
            }
        } catch (error) {
            console.error('Failed to record system log for export:', error);
        }
    };

    const handleExport = () => {
        const currentDate = new Date().toISOString().split('T')[0];
        const reportType = "Enrollment Summary"; // Specific report type
        const fileName = `${reportType.toLowerCase().replace(/\s+/g, '-')}-report-${currentDate}.pdf`;

        const adminUserID = localStorage.getItem('userID');
        const adminFullName = localStorage.getItem('fullName');
        const adminRole = localStorage.getItem('role');

        if (!adminUserID || !adminFullName || !adminRole) {
            notification.error({ message: 'Export Error', description: 'Admin user details not found.' });
            return;
        }

        // CALL THE NEW BACKEND ENDPOINT
        const exportUrl = `${process.env.REACT_APP_API_URL}/api/admin/export/enrollment-summary?userID=${encodeURIComponent(adminUserID)}&fullName=${encodeURIComponent(adminFullName)}&role=${encodeURIComponent(adminRole)}`;

        console.log("Exporting Enrollment Summary from URL:", exportUrl);

        fetch(exportUrl, { method: 'GET' })
            .then(response => {
                if (!response.ok) {
                    return response.text().then(text => {
                        let errorMsg = `Network response was not ok (${response.status})`;
                        try { const errorData = JSON.parse(text); if (errorData && errorData.message) errorMsg = errorData.message; }
                        catch (e) { if (text) errorMsg = text; }
                        throw new Error(errorMsg);
                    });
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                notification.success({
                    message: 'Export Successful',
                    description: `${fileName} has been downloaded.`,
                });

                // Log the specific export action
                createLog('Export', `Exported ${reportType} report: ${fileName}`);
            })
            .catch(error => {
                console.error(`${reportType} Export failed:`, error);
                notification.error({
                    message: 'Export Failed',
                    description: error.message || `There was an error exporting the ${reportType.toLowerCase()} report.`,
                });
            });
    };

    const handleUpdateEnrollmentStatus = async (record, newStatus, actionVerb) => {
        const confirmed = window.confirm(`Are you sure you want to mark this enrollment as "${newStatus}" for student ${record.fullName}?`);
        if (!confirmed) return;

        setLoading(true); // Indicate loading during update
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments/${record._id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enrollmentStatus: newStatus, officialEnrolledStatus: newStatus }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to update enrollment status`);
            }
            notification.success({ message: 'Status Updated', description: `Enrollment for ${record.fullName} marked as ${newStatus}.` });
            createLog(`Update Enrollment Status`, `Marked enrollment for ${record.studentID} (${record.fullName}) as "${newStatus}" (Action: ${actionVerb})`);
            fetchEnrollments(); // Refresh all data, which will then be processed by useMemo
        } catch (error) {
            console.error(`Error updating enrollment status:`, error);
            notification.error({ message: `Update Failed`, description: error.message });
        } finally {
            setLoading(false);
        }
    };

    // --- Table Column Definitions ---
    const columns = [
        {
            title: 'Student ID',
            width: 130,
            dataIndex: 'studentID',
            key: 'studentID',
            sorter: true, // Let AntD manage sorter state, actual sort in useMemo
            sortOrder: sorter.columnKey === 'studentID' ? sorter.order : null,
        },
        {
            title: 'Full Name',
            width: 220,
            dataIndex: 'fullName',
            key: 'fullName',
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{text || 'N/A'}</div>
                    {record.email && (<div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>)}
                </div>
            ),
            sorter: true,
            sortOrder: sorter.columnKey === 'fullName' ? sorter.order : null,
        },
        {
            title: 'Academic Year',
            width: 120,
            dataIndex: 'academicYear',
            key: 'academicYear',
            sorter: true,
            sortOrder: sorter.columnKey === 'academicYear' ? sorter.order : null,
        },
        {
            title: 'Term',
            width: 80,
            dataIndex: 'academicTerm',
            key: 'academicTerm',
            filters: [{ text: '1st', value: '1st' }, { text: '2nd', value: '2nd' }],
            filteredValue: tableFilters.academicTerm || null, // Controlled by tableFilters state
            sorter: true,
            sortOrder: sorter.columnKey === 'academicTerm' ? sorter.order : null,
        },
        {
            title: 'Grade Level',
            width: 110,
            dataIndex: 'academicLevel',
            key: 'academicLevel',
            sorter: true,
            sortOrder: sorter.columnKey === 'academicLevel' ? sorter.order : null,
        },
        {
            title: 'Strand',
            width: 100,
            dataIndex: 'academicStrand',
            key: 'academicStrand',
            sorter: true,
            sortOrder: sorter.columnKey === 'academicStrand' ? sorter.order : null,
        },
        {
            title: 'Enroll. Status',
            width: 130,
            dataIndex: 'enrollmentStatus',
            key: 'enrollmentStatus',
            filters: [
                { text: 'Complete', value: 'Complete' }, { text: 'Incomplete', value: 'Incomplete' },
                { text: 'Pending', value: 'Pending' }, { text: 'Withdrawn', value: 'Withdrawn' },
                { text: 'Dropped', value: 'Dropped' },
            ],
            filteredValue: tableFilters.enrollmentStatus || null,
            render: (status) => {
                let color = 'default';
                switch (status) {
                    case 'Complete': color = 'green'; break;
                    case 'Incomplete': color = 'orange'; break;
                    case 'Pending': color = 'blue'; break;
                    case 'Withdrawn': color = 'red'; break;
                    case 'Dropped': color = 'volcano'; break;
                    default: color = 'default'; break;
                }
                return <Tag color={color}>{status || 'N/A'}</Tag>;
            },
            sorter: true,
            sortOrder: sorter.columnKey === 'enrollmentStatus' ? sorter.order : null,
        },
        {
            title: 'Payment Ref #',
            width: 180,
            dataIndex: ['paymentInfo', 'referenceNumber'], // For nested data
            key: 'paymentRef', // Unique key for this column for sorter/filter state
            sorter: true,
            sortOrder: sorter.columnKey === 'paymentRef' ? sorter.order : null,
        },
        {
            title: 'Enrolled Date',
            width: 160,
            dataIndex: 'enrolledDate',
            key: 'enrolledDate',
            render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm') : 'N/A',
            sorter: true,
            sortOrder: sorter.columnKey === 'enrolledDate' ? sorter.order : null,
        },
        // {
        //     title: 'Action',
        //     key: 'action',
        //     fixed: 'right',
        //     width: 180,
        //     align: 'center',
        //     render: (_, record) => (
        //         <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
        //             <Tooltip title="View full student enrollment details">
        //                 <Button
        //                     icon={<FaEye />}
        //                     onClick={() => navigate(`/admin/manage-enrollments/view/${record._id}`)}
        //                     style={{ width: '150px' }}
        //                 >
        //                     View Details
        //                 </Button>
        //             </Tooltip>
        //             <Tooltip title="Assign or update student section">
        //                 <Button
        //                     onClick={() => navigate(`/admin/manage-enrollments/assign-section/${record._id}`)}
        //                     style={{ width: '150px' }}
        //                 >
        //                     Assign Section
        //                 </Button>
        //             </Tooltip>
        //             {(record.enrollmentStatus === 'Complete' || record.enrollmentStatus === 'Pending' || record.enrollmentStatus === 'Incomplete') && (
        //                 <>
        //                     <Tooltip title="Mark student enrollment as Withdrawn">
        //                         <Button
        //                             onClick={() => handleUpdateEnrollmentStatus(record, 'Withdrawn', 'Withdraw')}
        //                             danger
        //                             style={{ width: '150px' }}
        //                         >
        //                             Mark Withdrawn
        //                         </Button>
        //                     </Tooltip>
        //                     <Tooltip title="Mark student enrollment as Dropped">
        //                         <Button
        //                             onClick={() => handleUpdateEnrollmentStatus(record, 'Dropped', 'Drop')}
        //                             danger
        //                             ghost
        //                             style={{ width: '150px' }}
        //                         >
        //                             Mark Dropped
        //                         </Button>
        //                     </Tooltip>
        //                 </>
        //             )}
        //         </div>
        //     )
        // }
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <p className="heading">Manage Enrollments</p>
                    </div>

                    <div className="table-functions">
                        <div className="left-tools">
                            <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filters</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={handleExport}>Export</Button>
                        </div>
                        <div className="right-tools">
                            <AutoComplete
                                value={searchTerm}
                                options={autocompleteOptions}
                                style={{ width: 300 }}
                                onSearch={handleAutocompleteSuggest}    // Generates suggestions
                                onChange={handleSearchInputChange}    // Updates searchTerm on typing
                                onSelect={handleSearchInputChange}   // Updates searchTerm on selection
                                placeholder="Search Enrollments..."
                                allowClear
                            >
                                <Input suffix={<FaSearch style={{ color: '#aaa' }} />} />
                            </AutoComplete>
                             <Button type="primary" className="create-btn" icon={<FaPlus />}>
                                        Create Enrollment
                            </Button>
                        </div>
                    </div>

                    <Table
                        style={{ width: '100%', flex: 1, marginTop: '16px' }}
                        columns={columns}
                        dataSource={dataSource}
                        loading={loading}
                        scroll={{ x: 1800 }}
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} enrollments`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        bordered
                        onChange={(pagination, filters, sorterFromTable) => {
                            setTableFilters(filters);       // Update state for AntD column filters
                            setSorter(sorterFromTable);     // Update state for AntD column sorter
                            // useMemo will automatically re-calculate 'dataSource'
                        }}
                    />
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default ManageEnrollment;